import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Building2, Wallet, CheckCircle2, ArrowRight } from 'lucide-react';

const services = [
  {
    id: 'subsidies',
    icon: <FileText className="w-10 h-10" />,
    title: 'Government Subsidies',
    subtitle: 'State & Central Government Incentives',
    description: 'A subsidy or government incentive is a form of financial aid extended to businesses to generate employment and promote economic growth. We offer single-window solutions that add unique value to your business with customized approaches for MSMEs.',
    features: [
      'Capital Subsidy schemes',
      'Interest Subsidy programs',
      'State Government incentives',
      'Central Government schemes',
      'MSME-specific benefits',
      'Industry-specific subsidies',
      'Employment-linked incentives',
      'Export promotion schemes'
    ],
    image: 'https://images.pexels.com/photos/1407487/pexels-photo-1407487.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    id: 'sme-loans',
    icon: <Building2 className="w-10 h-10" />,
    title: 'SME & Project Loans',
    subtitle: 'Business Financing Solutions',
    description: 'Raising finance is the most significant challenge entrepreneurs face in today\'s competitive landscape. We help clients secure adequate financing from various sources at optimal rates and favorable terms, supporting new projects, expansion, and working capital needs.',
    features: [
      'New Project financing',
      'Business expansion loans',
      'Working capital funding',
      'Equipment financing',
      'Term loans',
      'MUDRA loans',
      'CGTMSE-backed loans',
      'Cash credit facilities'
    ],
    image: 'https://images.unsplash.com/photo-1554290671-aa496cf17b4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdsYXNzJTIwZ2VvbWV0cmljJTIwYXJjaGl0ZWN0dXJlJTIwdGVhbCUyMGJsdWV8ZW58MHx8fHwxNzcxMzkxMzU0fDA&ixlib=rb-4.1.0&q=85'
  },
  {
    id: 'retail-loans',
    icon: <Wallet className="w-10 h-10" />,
    title: 'Retail Loans',
    subtitle: 'Personal & Business Financing',
    description: 'Through our extensive network of partner banks and NBFCs, we bring you the best deals in retail loans. Capital flow is the foundation of any successful business, and we ensure our customers get the best finance solutions to scale to new heights.',
    features: [
      'Home Loans',
      'Loan Against Property (LAP)',
      'Business Loans',
      'Personal Loans',
      'Vehicle financing',
      'Education loans',
      'Overdraft facilities',
      'Balance transfer options'
    ],
    image: 'https://images.pexels.com/photos/955394/pexels-photo-955394.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  }
];

export const ServicesDetail = () => (
  <>
    {services.map((service, index) => (
      <section
        key={service.id}
        id={service.id}
        className={`py-24 ${index % 2 === 1 ? 'bg-[#f8fafc]' : 'bg-white'}`}
        data-testid={`service-${service.id}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
            <motion.div
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={index % 2 === 1 ? 'lg:order-2' : ''}
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3b5998] to-[#00bcd4] flex items-center justify-center text-white mb-6">
                {service.icon}
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0f172a] mb-2">
                {service.title}
              </h2>
              <p className="text-[#00bcd4] font-medium mb-6">{service.subtitle}</p>
              <p className="text-[#64748b] leading-relaxed mb-8">
                {service.description}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {service.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00bcd4] flex-shrink-0" />
                    <span className="text-[#0f172a] text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                data-testid={`service-cta-${service.id}`}
                className="inline-flex items-center gap-2 gradient-btn px-8 py-4 rounded-full font-medium text-white mt-8"
              >
                Get Consultation
                <ArrowRight size={18} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={index % 2 === 1 ? 'lg:order-1' : ''}
            >
              <div className="relative">
                <img
                  src={service.image}
                  alt={service.title}
                  className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-[#3b5998] to-[#00bcd4] rounded-2xl opacity-20"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    ))}
  </>
);
